import { useState, useEffect } from "react";
import styles from "./CabinClasses.module.css";
import AddCabinClassModal from "../../../../components/Products/Flights/CabinClasses/AddCabinClassModal";
import { Search, Download, RefreshCw, ArrowUpDown, Armchair } from "lucide-react";
import {
  useGetCabinClasses,
  useCreateCabinClass,
  useUpdateCabinClass,
  useDeleteCabinClass,
} from "../../../../api/products/useCabinClassesApi";

const CabinClasses = () => {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCabinClass, setEditingCabinClass] = useState(null);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: cabinClasses = [], pagination, loading, error, refetch } = useGetCabinClasses({
    page: 1,
    limit: 500,
    search: debouncedSearch,
    status: statusFilter,
  });

  const { execute: createCabinClass } = useCreateCabinClass();
  const { execute: updateCabinClass } = useUpdateCabinClass();
  const { execute: deleteCabinClass } = useDeleteCabinClass();

  const filteredCabinClasses = [...cabinClasses].sort((a, b) => a.rank - b.rank);

  const handleEdit = (cabinClass) => {
    setEditingCabinClass(cabinClass);
    setApiError(null);
    setShowAddModal(true);
  };

  const handleDelete = async (cabinClassId) => {
    if (window.confirm("Are you sure you want to delete this cabin class?")) {
      const result = await deleteCabinClass(cabinClassId);
      if (result.success) {
        refetch();
      } else {
        alert(result.message || "Failed to delete cabin class");
      }
    }
  };

  const handleSaveCabinClass = async (cabinClassData) => {
    setApiError(null);
    if (editingCabinClass) {
      const result = await updateCabinClass(editingCabinClass.id, cabinClassData);
      if (result.success) {
        refetch();
        handleCloseModal();
        return;
      }
      setApiError(result.message);
      return result.message;
    }
    const result = await createCabinClass(cabinClassData);
    if (result.success) {
      refetch();
      handleCloseModal();
      return;
    }
    setApiError(result.message);
    return result.message;
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingCabinClass(null);
    setApiError(null);
  };

  const handleAddNew = () => {
    setEditingCabinClass(null);
    setApiError(null);
    setShowAddModal(true);
  };

  const handleExport = () => {
    console.log("EXPORT CABIN CLASSES DATA");
    // Export functionality
  };

  // Get rank badge color
  const getRankColor = (rank) => {
    if (rank === 1) return "#f59e0b"; // Gold for First
    if (rank === 2) return "#6366f1"; // Purple for Business
    if (rank === 3) return "#3b82f6"; // Blue for Premium Economy
    return "#6b7280"; // Gray for others
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1>Cabin Classes</h1>
          <p className={styles.subtitle}>Manage cabin class master data for flight bookings and fare configurations</p>
        </div>
        <button className={styles.addBtn} onClick={handleAddNew}>
          + Add Cabin Class
        </button>
      </div>

      {/* Info Banner */}
      <div className={styles.infoBanner}>
        <div className={styles.infoIcon}>ℹ️</div>
        <div className={styles.infoText}>
          <strong>Master Data Notice:</strong> Cabin classes define service levels for flight bookings. Rank determines the display order in search results and booking flows. Lower rank numbers appear first.
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className={styles.infoBanner} style={{ background: "#fef2f2", borderColor: "#fecaca" }}>
          <div className={styles.infoIcon}>⚠️</div>
          <div className={styles.infoText}>
            {error}
            <button className={styles.retryBtn} onClick={() => refetch()}>Retry</button>
          </div>
        </div>
      )}

      {/* Filters & Actions */}
      <div className={styles.controls}>
        <div className={styles.searchBox}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by cabin class name or code..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className={styles.filters}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button className={styles.iconBtn} onClick={() => refetch()} title="Refresh">
            <RefreshCw size={18} />
          </button>

          <button className={styles.iconBtn} onClick={handleExport} title="Export">
            <Download size={18} />
          </button>
        </div>
      </div>

      {/* Results Count */}
      <div className={styles.resultsCount}>
        Showing {filteredCabinClasses.length} of {pagination?.total ?? cabinClasses.length} cabin classes
      </div>

      {/* Cabin Classes Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>
                <div className={styles.thContent}>
                  RANK
                  <ArrowUpDown size={14} />
                </div>
              </th>
              <th>CABIN CLASS NAME</th>
              <th>CABIN CLASS CODE</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className={styles.noData}>
                  <div className={styles.noDataContent}>
                    <p>Loading cabin classes…</p>
                  </div>
                </td>
              </tr>
            ) : filteredCabinClasses.length === 0 ? (
              <tr>
                <td colSpan="5" className={styles.noData}>
                  <div className={styles.noDataContent}>
                    <div className={styles.noDataIcon}>💺</div>
                    <p>No cabin classes found</p>
                    <span>Try adjusting your search or filters</span>
                  </div>
                </td>
              </tr>
            ) : (
              filteredCabinClasses.map(cabinClass => (
                <tr key={cabinClass.id}>
                  <td>
                    <div 
                      className={styles.rankBadge} 
                      style={{ 
                        background: `${getRankColor(cabinClass.rank)}15`,
                        color: getRankColor(cabinClass.rank)
                      }}
                    >
                      <ArrowUpDown size={14} />
                      {cabinClass.rank}
                    </div>
                  </td>
                  <td>
                    <div className={styles.cabinName}>
                      <div className={styles.cabinIcon}>
                        <Armchair size={18} />
                      </div>
                      <strong>{cabinClass.cabinClassName}</strong>
                    </div>
                  </td>
                  <td>
                    <span className={styles.codeTag}>{cabinClass.cabinClassCode}</span>
                  </td>
                  <td>
                    <span
                      className={`${styles.statusBadge} ${
                        cabinClass.status === "active"
                          ? styles.statusActive
                          : styles.statusInactive
                      }`}
                    >
                      {cabinClass.status === "active" ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.editBtn}
                        onClick={() => handleEdit(cabinClass)}
                        title="Edit"
                      >
                       Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <AddCabinClassModal
        open={showAddModal}
        onClose={handleCloseModal}
        onSave={handleSaveCabinClass}
        cabinClass={editingCabinClass}
        apiError={apiError}
      />
    </div>
  );
};

export default CabinClasses;